import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProfileDetailModal = ({ 
  profile, 
  onClose, 
  onSendMessage, 
  onExpressInterest,
  userGender = 'male',
  hasActiveSubscription = false 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isInterested, setIsInterested] = useState(false);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? profile?.photos?.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === profile?.photos?.length - 1 ? 0 : prev + 1
    );
  };

  const handleExpressInterest = () => {
    setIsInterested(!isInterested);
    onExpressInterest(profile?.id);
  };

  const handleSendMessage = () => {
    if (userGender === 'female' || hasActiveSubscription) {
      onSendMessage(profile?.id);
      onClose();
    } else {
      window.location.href = '/subscription-payment';
    }
  };

  const getBlurClass = () => {
    switch (profile?.privacyLevel) {
      case 'high':
        return 'blur-lg';
      case 'medium':
        return 'blur-md';
      case 'low':
        return 'blur-sm';
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/95 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-card rounded-xl shadow-warm-2xl overflow-hidden animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-card/90 backdrop-blur-sm rounded-full hover:bg-card transition-smooth"
        >
          <Icon name="X" size={20} />
        </button>

        <div className="flex flex-col lg:flex-row max-h-[90vh]">
          <div className="relative lg:w-1/2 h-64 lg:h-auto overflow-hidden">
            {(!profile?.photos?.length || !profile?.photos?.[currentImageIndex]) ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-200 via-slate-100 to-primary/30 dark:from-slate-700 dark:via-slate-600 dark:to-primary/40">
                <div className="w-24 h-24 rounded-full bg-slate-300/80 dark:bg-slate-500/80 flex items-center justify-center mb-2">
                  <Icon name="User" size={48} className="text-slate-500 dark:text-slate-300" />
                </div>
                <span className="text-base font-medium text-slate-600 dark:text-slate-300 max-w-[80%] truncate px-2">
                  {profile?.displayName || 'No photo'}
                </span>
              </div>
            ) : (
              <Image
                src={profile?.photos?.[currentImageIndex]}
                alt={profile?.photosAlt?.[currentImageIndex]}
                className={`w-full h-full object-cover ${
                  profile?.photoBlurred ? getBlurClass() : ''
                }`}
              />
            )}

            {profile?.photos?.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-card/90 backdrop-blur-sm rounded-full hover:bg-card transition-smooth"
                >
                  <Icon name="ChevronLeft" size={20} />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-card/90 backdrop-blur-sm rounded-full hover:bg-card transition-smooth"
                >
                  <Icon name="ChevronRight" size={20} />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {profile?.photos?.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-smooth ${
                        index === currentImageIndex
                          ? 'bg-white w-6' :'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}

            {profile?.isVerified && (
              <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-primary/90 backdrop-blur-sm rounded-full">
                <Icon name="BadgeCheck" size={16} color="#FFFFFF" />
                <span className="text-sm font-medium text-white">Verified</span>
              </div>
            )}
          </div>

          <div className="lg:w-1/2 overflow-y-auto">
            <div className="p-6 space-y-6">
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-2xl md:text-3xl font-heading font-semibold">
                        {profile?.displayName}
                      </h2>
                      {profile?.isOnline && (
                        <span className="w-3 h-3 bg-success rounded-full flex-shrink-0 shadow-sm border border-background" title="Online now" />
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Icon name="Calendar" size={16} />
                        <span className="text-sm">{profile?.age} years</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="MapPin" size={16} />
                        <span className="text-sm">{profile?.location}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="Users" size={16} />
                        <span className="text-sm">{profile?.relationshipStatus}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-success/10 rounded-lg">
                    <Icon name="Heart" size={16} color="var(--color-success)" />
                    <span className="text-sm font-bold text-success">{profile?.compatibility}%</span>
                  </div>
                </div>

                {profile?.lastActive && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>Active {profile?.lastActive}</span>
                  </div>
                )}
              </div>

              {profile?.bio && (
                <div>
                  <h3 className="text-sm font-caption font-semibold mb-2 flex items-center gap-2">
                    <Icon name="FileText" size={16} color="var(--color-primary)" />
                    About
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {profile?.bio}
                  </p>
                </div>
              )}

              {profile?.interests && profile?.interests?.length > 0 && (
                <div>
                  <h3 className="text-sm font-caption font-semibold mb-3 flex items-center gap-2">
                    <Icon name="Heart" size={16} color="var(--color-primary)" />
                    Interests
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile?.interests?.map((interest, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-muted rounded-full text-sm font-medium"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile?.lookingFor && (
                <div>
                  <h3 className="text-sm font-caption font-semibold mb-2 flex items-center gap-2">
                    <Icon name="Target" size={16} color="var(--color-primary)" />
                    Looking For
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {profile?.lookingFor}
                  </p>
                </div>
              )}

              <div className="pt-4 border-t border-border space-y-3">
                <div className="flex gap-3">
                  <Button
                    variant={isInterested ? 'default' : 'outline'}
                    fullWidth
                    iconName={isInterested ? 'HeartOff' : 'Heart'}
                    iconPosition="left"
                    onClick={handleExpressInterest}
                  >
                    {isInterested ? 'Interested' : 'Express Interest'}
                  </Button>
                  <Button
                    variant="default"
                    fullWidth
                    iconName="MessageCircle"
                    iconPosition="left"
                    onClick={handleSendMessage}
                  >
                    Send Message
                  </Button>
                </div>

                {userGender === 'male' && !hasActiveSubscription && (
                  <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg">
                    <Icon name="Lock" size={20} color="var(--color-warning)" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-warning mb-1">
                        Premium Feature
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Subscribe to send unlimited messages and unlock all features
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailModal;